import React, { useState, useRef, useEffect, useCallback } from "react";
import { Offcanvas, Spinner } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaPaperPlane,
  FaTrash,
  FaDownload,
} from "react-icons/fa";
import { LuSparkle } from "react-icons/lu";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { askChatbot } from "../api/overviewApi";

// ── Cited-tickets CSV export ────────────────────────────────────────────────
// Built entirely client-side from data the chatbot API already returns —
// no server round-trip, since this data is ephemeral (tied to one answer)
// rather than a stable, re-queryable report.
const CITED_TICKET_CSV_COLUMNS = [
  ["ticket_number", "Ticket Number"],
  ["title", "Title"],
  ["description", "Description"],
  ["status", "Status"],
  ["priority", "Priority"],
  ["queue", "Queue"],
  ["client", "Client"],
  ["created", "Created"],
  ["due", "Due"],
  ["ticket_type", "Ticket Type"],
  ["hours_worked", "Hours Worked"],
  ["billed_hours", "Billed Hours"],
  ["resources", "Resources"],
];

const csvEscape = (value) => {
  const str = value == null ? "" : String(value);
  const safe = /^[=+\-@]/.test(str) ? `'${str}` : str;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const citedTicketsToCsv = (tickets) => {
  const header = CITED_TICKET_CSV_COLUMNS.map(([, label]) => csvEscape(label)).join(",");
  const rows = tickets.map(t =>
    CITED_TICKET_CSV_COLUMNS.map(([key]) => csvEscape(t[key])).join(",")
  );
  return [header, ...rows].join("\r\n");
};

const downloadCsv = (filename, csvString) => {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const SUGGESTED_PROMPTS = [
  "What are the oldest open tickets?",
  "Which queue has the most backlog?",
  "Are there any emergency or critical tickets open?",
  "What's the priority breakdown right now?",
  "Which tickets have been open the longest?",
  "What should we focus on first?",
];

// ── Message bubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div style={{
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom: 10,
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg, #6f42c1, #5a32a3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginRight: 8, marginTop: 2,
        }}>
          <LuSparkle size={13} color="#fff" />
        </div>
      )}
      <div style={{
        maxWidth: "82%",
        padding: "10px 13px",
        borderRadius: isUser ? "14px 14px 4px 14px" : "4px 14px 14px 14px",
        background: isUser
          ? "linear-gradient(135deg, #6f42c1, #5a32a3)"
          : "var(--color-bg-hover)",
        color: isUser ? "#fff" : "var(--color-text)",
        fontSize: 13,
        lineHeight: 1.6,
        wordBreak: "break-word",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}>
        {isUser ? msg.content : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p:      ({ node, ...p }) => <p style={{ margin: "4px 0" }} {...p} />,
              ul:     ({ node, ...p }) => <ul style={{ margin: "6px 0", paddingLeft: 18 }} {...p} />,
              ol:     ({ node, ...p }) => <ol style={{ margin: "6px 0", paddingLeft: 18 }} {...p} />,
              li:     ({ node, ...p }) => <li style={{ margin: "3px 0" }} {...p} />,
              strong: ({ node, ...p }) => <strong style={{ color: "var(--color-primary)", fontWeight: 700 }} {...p} />,
              code:   ({ node, inline, ...p }) => inline
                ? <code style={{ background: "rgba(111,66,193,0.1)", padding: "1px 5px", borderRadius: 3, fontSize: 12 }} {...p} />
                : <pre style={{ background: "rgba(0,0,0,0.06)", padding: 10, borderRadius: 6, overflow: "auto", fontSize: 12, margin: "6px 0" }} {...p} />,
            }}
          >
            {msg.content}
          </ReactMarkdown>
        )}
        {!isUser && msg.sources && msg.sources.length > 0 && (
          <div style={{
            marginTop: 8, paddingTop: 8,
            borderTop: "1px solid var(--color-border)",
            fontSize: 11, color: "var(--color-text-light)",
          }}>
            Sources: {msg.sources.slice(0, 5).map(s => s.ticket_number).filter(Boolean).join(", ")}
          </div>
        )}
        {!isUser && msg.citedTickets && msg.citedTickets.length > 0 && (
          <button
            onClick={() => downloadCsv(
              `cited-tickets-${Date.now()}.csv`,
              citedTicketsToCsv(msg.citedTickets)
            )}
            style={{
              marginTop: 8, display: "flex", alignItems: "center", gap: 6,
              fontSize: 11.5, color: "#6f42c1", background: "none",
              border: "1px solid var(--color-border)", borderRadius: 6,
              padding: "5px 9px", cursor: "pointer",
            }}
          >
            <FaDownload size={10} />
            Download CSV ({msg.citedTickets.length} ticket{msg.citedTickets.length !== 1 ? "s" : ""})
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function OverviewChatbot({ clientId }) {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [open, messages.length]);

  // Reset chat when client changes
  useEffect(() => { setMessages([]); }, [clientId]);

  const sendMessage = useCallback(async (question) => {
    const q = (question || input).trim();
    if (!q || !clientId || loading) return;

    const userMsg = { role: "user", content: q };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    const history = messages
      .slice(-8)
      .map(({ role, content }) => ({ role, content }));
    const result = await askChatbot(history, q, clientId);

    if (result.answer) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: result.answer,
        sources: result.sources,
        citedTickets: result.citedTickets,
      }]);
    } else {
      setMessages(prev => [...prev, { role: "assistant", content: `⚠️ ${result.error || "Something went wrong."}` }]);
    }
    setLoading(false);
  }, [clientId, input, loading, messages]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  if (!clientId) return null;

  return (
    <>
      {/* ── Floating button — hidden while sidebar is open ── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            onClick={() => setOpen(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0, transition: { duration: 0.15 } }}
            transition={{ delay: 1.2, type: "spring", stiffness: 200, damping: 18 }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            style={{
              position: "fixed",
              bottom: 28,
              right: 28,
              zIndex: 1049,
              background: "linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%)",
              border: "none",
              borderRadius: 50,
              padding: "12px 22px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              boxShadow: "0 6px 24px rgba(111,66,193,0.45)",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "0.01em",
            }}
          >
            <LuSparkle size={16} />
            Ask AI
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Side panel ── */}
      <Offcanvas
        show={open}
        onHide={() => setOpen(false)}
        placement="end"
        style={{ width: 430, display: "flex", flexDirection: "column", background: "var(--color-bg-card)", color: "var(--color-text)" }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 18px",
          borderBottom: "1px solid var(--color-border)",
          background: "linear-gradient(135deg, rgba(111,66,193,0.08) 0%, rgba(111,66,193,0.03) 100%)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: "linear-gradient(135deg, #6f42c1, #5a32a3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <LuSparkle size={16} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--color-text)" }}>
                OneView Assistant
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 1 }}>
                Powered by live ticket data
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {messages.length > 0 && (
              <button
                onClick={() => setMessages([])}
                title="Clear conversation"
                style={{
                  background: "none", border: "1px solid var(--color-border)",
                  borderRadius: 7, padding: "5px 8px", cursor: "pointer",
                  color: "var(--color-text-light)", display: "flex", alignItems: "center", gap: 5,
                  fontSize: 12,
                }}
              >
                <FaTrash size={11} /> Clear
              </button>
            )}
            <button
              type="button"
              aria-label="Close chatbot"
              onClick={() => setOpen(false)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "5px 7px", borderRadius: 7, color: "var(--color-text-light)",
                display: "flex", alignItems: "center",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--color-bg-hover)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <FaTimes size={15} />
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "16px 16px 8px",
          display: "flex", flexDirection: "column",
        }}>

          {/* Empty state — suggested prompts */}
          {messages.length === 0 && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div style={{
                  background: "linear-gradient(135deg, rgba(111,66,193,0.07) 0%, rgba(111,66,193,0.03) 100%)",
                  borderRadius: 12, padding: "16px 14px", marginBottom: 18,
                  border: "1px solid rgba(111,66,193,0.12)",
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)", marginBottom: 4 }}>
                    Hi! I can answer questions about your open tickets.
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text-light)" }}>
                    I have live access to priority, queue, status, and ticket age data.
                  </div>
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-light)", marginBottom: 10, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  Suggested questions
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {SUGGESTED_PROMPTS.map(prompt => (
                    <button
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      style={{
                        background: "var(--color-bg-card)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 10,
                        padding: "10px 14px",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "var(--color-text)",
                        textAlign: "left",
                        transition: "all 0.15s",
                        display: "flex", alignItems: "center", gap: 8,
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = "rgba(111,66,193,0.4)";
                        e.currentTarget.style.background  = "rgba(111,66,193,0.05)";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = "var(--color-border)";
                        e.currentTarget.style.background  = "var(--color-bg-card)";
                      }}
                    >
                      <LuSparkle size={12} style={{ color: "#6f42c1", flexShrink: 0 }} />
                      {prompt}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {/* Chat messages */}
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageBubble msg={msg} />
            </motion.div>
          ))}

          {/* Thinking indicator */}
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "linear-gradient(135deg, #6f42c1, #5a32a3)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <LuSparkle size={13} color="#fff" />
              </div>
              <div style={{
                background: "var(--color-bg-hover)", borderRadius: "4px 14px 14px 14px",
                padding: "10px 14px", display: "flex", alignItems: "center", gap: 6,
                fontSize: 13, color: "var(--color-text-light)",
              }}>
                <Spinner animation="border" size="sm" style={{ width: 13, height: 13, borderWidth: 2, color: "#6f42c1" }} />
                Thinking…
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div style={{
          borderTop: "1px solid var(--color-border)",
          padding: "12px 14px",
          background: "var(--color-bg-card)",
          flexShrink: 0,
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your tickets…"
            disabled={loading}
            rows={1}
            style={{
              flex: 1,
              padding: "9px 12px",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              fontSize: 13,
              background: "var(--color-bg-card)",
              color: "var(--color-text)",
              resize: "none",
              outline: "none",
              lineHeight: 1.5,
              maxHeight: 100,
              overflow: "auto",
              transition: "border-color 0.15s",
            }}
            onFocus={e => e.target.style.borderColor = "#6f42c1"}
            onBlur={e  => e.target.style.borderColor = "var(--color-border)"}
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            style={{
              padding: "9px 12px",
              background: input.trim() && !loading ? "#6f42c1" : "var(--color-bg-hover)",
              border: "none",
              borderRadius: 10,
              color: input.trim() && !loading ? "#fff" : "var(--color-text-light)",
              cursor: input.trim() && !loading ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s",
              flexShrink: 0,
            }}
          >
            <FaPaperPlane size={14} />
          </button>
        </div>
      </Offcanvas>
    </>
  );
}

export default OverviewChatbot;
